import { useCallback, useState } from 'react';
import {
    createFaqInDb,
    deleteFaqFromDb,
    fetchFaqsFromDb,
    updateFaqInDb,
    type CreateFaqPayload,
    type Faq,
    type UpdateFaqPayload,
} from '../services/faqService';
import { getErrorMessage } from '../utils/errors';

export function useFaqs() {
    const [faqs,    setFaqs]    = useState<Faq[]>([]);
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState<string | null>(null);

    const fetchFaqs = useCallback(async (): Promise<Faq[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchFaqsFromDb();
            setFaqs(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch FAQs'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createFaq = useCallback(async (payload: CreateFaqPayload): Promise<Faq | null> => {
        setError(null);
        try {
            const created = await createFaqInDb(payload);
            setFaqs(prev =>
                [...prev, created].sort((a, b) =>
                    a.category.localeCompare(b.category) || a.sort_order - b.sort_order
                )
            );
            return created;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create FAQ'));
            return null;
        }
    }, []);

    const updateFaq = useCallback(async (
        id: string,
        patch: UpdateFaqPayload
    ): Promise<Faq | null> => {
        setError(null);
        try {
            const updated = await updateFaqInDb(id, patch);
            setFaqs(prev =>
                prev
                    .map(faq => faq.id === id ? updated : faq)
                    .sort((a, b) =>
                        a.category.localeCompare(b.category) || a.sort_order - b.sort_order
                    )
            );
            return updated;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update FAQ'));
            return null;
        }
    }, []);

    const toggleFaqActive = useCallback(async (faq: Faq): Promise<boolean> => {
        setError(null);
        try {
            const updated = await updateFaqInDb(faq.id, { is_active: !faq.is_active });
            setFaqs(prev => prev.map(item => item.id === faq.id ? updated : item));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to toggle FAQ'));
            return false;
        }
    }, []);

    const deleteFaq = useCallback(async (id: string): Promise<boolean> => {
        setError(null);
        try {
            await deleteFaqFromDb(id);
            setFaqs(prev => prev.filter(faq => faq.id !== id));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete FAQ'));
            return false;
        }
    }, []);

    return { faqs, loading, error, fetchFaqs, createFaq, updateFaq, toggleFaqActive, deleteFaq };
}